import { gate } from 'hub-http/gates';
import { userInfoSync } from 'hub-http/userInfo';
import { AI_SUPPORTED_ASSET_TYPES, ASSET_TYPES, SUPPORTED_ASSET_TYPES } from 'personalization-lib/constants/assetTypes';
import { READ_SCOPES, WRITE_SCOPES } from 'personalization-lib/constants/auth';
const safeGetUserInfo = () => {
  try {
    return userInfoSync();
  } catch (error) {
    return undefined;
  }
};
const getUserLimits = () => {
  var _safeGetUserInfo;
  return (_safeGetUserInfo = safeGetUserInfo()) === null || _safeGetUserInfo === void 0 || (_safeGetUserInfo = _safeGetUserInfo.portal) === null || _safeGetUserInfo === void 0 ? void 0 : _safeGetUserInfo.limits;
};
export const getStrategiesLimit = () => {
  var _getUserLimits;
  return ((_getUserLimits = getUserLimits()) === null || _getUserLimits === void 0 ? void 0 : _getUserLimits['personalization-max-strategies']) || 0;
};

// TODO: replace with quartz getGates() when all apps are migrated to quartz
let memoizedGates = undefined;
const getGates = () => {
  if (!memoizedGates) {
    var _safeGetUserInfo2;
    memoizedGates = new Set((_safeGetUserInfo2 = safeGetUserInfo()) === null || _safeGetUserInfo2 === void 0 ? void 0 : _safeGetUserInfo2.gates);
  }
  return memoizedGates;
};
export const hasGate = _gate => getGates().has(_gate);
export const getFeatureGates = () => ({
  webVisitors: hasGate(gate('CRM:Segments:WebVisitors')),
  websitePages: hasGate(gate('Personalize:WebsitePages')),
  contextLayer: hasGate(gate('Personalize:ContextLayerDemo')),
  imageGenInput: hasGate(gate('Personalize:GenerateImagesInput')),
  newNav: hasGate(gate('Personalize:NavRevamp'))
});

// TODO: replace with quartz getScopes() when all apps are migrated to quartz
let memoizedScopes = undefined;
const getScopes = () => {
  if (!memoizedScopes) {
    var _safeGetUserInfo3;
    memoizedScopes = new Set((_safeGetUserInfo3 = safeGetUserInfo()) === null || _safeGetUserInfo3 === void 0 || (_safeGetUserInfo3 = _safeGetUserInfo3.user) === null || _safeGetUserInfo3 === void 0 ? void 0 : _safeGetUserInfo3.scopes);
  }
  return memoizedScopes;
};
export const hasScope = scope => getScopes().has(scope);
export const hasScopes = scopes => scopes.every(scope => hasScope(scope));
export const getPersonalizationAccess = () => ({
  view: hasScope('personalize-view'),
  edit: hasScope('personalize-edit')
});

/**
 * Returns true if the user has write access to the asset type.
 *
 * @param assetType - The asset type to check.
 * @returns Returns true/false if the user has write access to the asset type.
 */
export const canEditAssetType = assetType => Boolean(WRITE_SCOPES[assetType] && hasScopes(WRITE_SCOPES[assetType]));

/**
 * Returns true if the user has read access to the asset type.
 *
 * @param assetType - The asset type to check.
 * @returns Returns true/false if the user has read access to the asset type.
 */
export const canReadAssetType = assetType => {
  const hasRequiredScope = Boolean(READ_SCOPES[assetType] && hasScope(READ_SCOPES[assetType]));
  if (assetType === ASSET_TYPES.WEBSITE_PAGE) {
    return hasRequiredScope && getFeatureGates().websitePages;
  }
  return hasRequiredScope;
};

/**
 * Returns the asset types that the user has write access to.
 *
 * @param assetTypes - The asset types to filter. Defaults to all supported asset types.
 * @returns The asset types that the user has write access to.
 */
export const getWritableAssetTypes = (assetTypes = [...SUPPORTED_ASSET_TYPES]) => assetTypes.filter(canEditAssetType);

/**
 * Returns the asset types that the user has read access to.
 *
 * @param assetTypes - The asset types to filter. Defaults to all supported asset types.
 * @returns The asset types that the user has read access to.
 */
export const getReadableAssetTypes = (assetTypes = [...SUPPORTED_ASSET_TYPES]) => assetTypes.filter(canReadAssetType);

/**
 * Returns true if AI variant creation is supported for the asset type.
 *
 * @param assetType - The asset type to check. If undefined, returns true.
 * @returns Returns true if AI is supported for the asset type or if no asset type is provided.
 */
export const canCreateAiVariantForAssetType = assetType => !assetType || AI_SUPPORTED_ASSET_TYPES.includes(assetType);

/** DO NOT USE: Exported only for testing purposes */
export const resetMemoizedAuth = () => {
  memoizedGates = undefined;
  memoizedScopes = undefined;
};